// components/ResumenDia.js
import { DollarSign, Users, Anchor, Wallet, CreditCard } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { esMedioEfectivo } from '../../utils/pagos';

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });

const ResumenDia = ({
  totalClientes,
  embarcacionesIngresadas,
  totalVentas,
  totalClientesAnulados = 0,
  totalVentasAnuladas = 0,
  ventasPorUsuario,
  extrasResumen = { activos: [], anulados: [], totales: {} },
  diasResumen = { activos: 0, anulados: 0, detalleActivos: [], detalleAnulados: [] },
  totalesFinancieros = { entradas: 0, extras: 0, totalGeneral: 0 },
  desgloseMediosPago = { detalle: [], totales: {} },
  onDesglose,
  onExportExcel
}) => {
  const extrasActivos = extrasResumen?.activos ?? [];
  const extrasAnulados = extrasResumen?.anulados ?? [];
  const totalExtrasCantidadActivos = Number(
    extrasResumen?.totales?.cantidadActivos ?? 0
  );
  const totalExtrasCantidadAnulados = Number(
    extrasResumen?.totales?.cantidadAnulados ?? 0
  );
  const totalExtrasMontoActivos = Number(
    extrasResumen?.totales?.montoActivos ?? 0
  );
  const totalExtrasMontoAnulados = Number(
    extrasResumen?.totales?.montoAnulados ?? 0
  );
  const totalEntradasMonto = Number(totalesFinancieros?.entradas ?? 0);
  const totalGeneralMonto = Number(
    totalesFinancieros?.totalGeneral ?? totalVentas ?? 0
  );
  const totalExtrasIngresos = Number(totalesFinancieros?.extras ?? totalExtrasMontoActivos);

  const totalesMedios = desgloseMediosPago?.totales ?? {};
  const efectivoTotales = totalesMedios.efectivo || { total: 0, entradas: 0, extras: 0 };
  const otrosTotales = totalesMedios.otros || { total: 0, entradas: 0, extras: 0 };
  const detalleMedios = Array.isArray(desgloseMediosPago?.detalle)
    ? desgloseMediosPago.detalle
    : [];

  return (
    <div className="bg-azul-claro bg-opacity-10 rounded-xl p-6 shadow-lg">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4">
        <h2 className="text-2xl font-semibold text-center md:text-left text-azul-claro">Resumen del Día</h2>
        {typeof onExportExcel === 'function' && (
          <button
            onClick={onExportExcel}
            className="px-4 py-2 bg-amarillo-dorado hover:bg-amber-500 text-azul-oscuro rounded-lg font-medium shadow"
          >
            Descargar Excel de hoy
          </button>
        )}
      </div>
      <div className="grid gap-6 md:grid-cols-4 mb-6">
        <div className="text-center">
          <p className="text-lg mb-1 text-gris-piedra">Total de clientes:</p>
          <p className="text-4xl font-bold text-azul-claro flex items-center justify-center">
            <Users className="mr-2" />
            {totalClientes}
          </p>
          {totalClientesAnulados > 0 && (
            <p className="text-sm text-red-300 mt-1">
              Anuladas: {totalClientesAnulados}
            </p>
          )}
        </div>
        <div className="text-center">
          <p className="text-lg mb-1 text-gris-piedra">Ingresos por entradas:</p>
          <p className="text-4xl font-bold text-amarillo-dorado flex items-center justify-center">
            <DollarSign className="mr-2" />
            ${formatCurrency(totalEntradasMonto)}
          </p>
          <p className="text-sm text-gris-piedra">Extras incluidos aparte</p>
        </div>
        <div className="text-center">
          <p className="text-lg mb-1 text-gris-piedra">Total general de ingresos:</p>
          <p className="text-4xl font-bold text-amarillo-dorado flex items-center justify-center">
            <Wallet className="mr-2" />
            ${formatCurrency(totalGeneralMonto)}
          </p>
          {totalVentasAnuladas > 0 && (
            <p className="text-sm text-red-300 mt-1">
              Anulados: ${formatCurrency(totalVentasAnuladas)}
            </p>
          )}
        </div>
        <div className="text-center">
          <p className="text-lg mb-1 text-gris-piedra">Total de embarcaciones:</p>
          <p className="text-4xl font-bold text-verde-esmeralda flex items-center justify-center">
            <Anchor className="mr-2" />
            {embarcacionesIngresadas || 0}
          </p>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 mb-6">
        <div className="text-center">
          <p className="text-lg mb-1 text-gris-piedra">Total de días vendidos:</p>
          <p className="text-3xl font-bold text-azul-claro">
            {Number(diasResumen.activos || 0).toLocaleString('es-AR', { minimumFractionDigits: 0 })}
          </p>
          {Number(diasResumen.anulados || 0) > 0 && (
            <p className="text-sm text-red-300 mt-1">
              Anulados: {Number(diasResumen.anulados || 0).toLocaleString('es-AR', { minimumFractionDigits: 0 })}
            </p>
          )}
          {Array.isArray(diasResumen.detalleActivos) && diasResumen.detalleActivos.length > 0 && (
            <p className="text-xs text-gris-piedra mt-2">
              {`Días activos: ${diasResumen.detalleActivos
                .map((dia) => format(parseISO(`${dia}T00:00:00`), 'dd/MM/yyyy'))
                .join(', ')}`}
            </p>
          )}
          {Array.isArray(diasResumen.detalleAnulados) && diasResumen.detalleAnulados.length > 0 && (
            <p className="text-xs text-red-300 mt-1">
              {`Días anulados: ${diasResumen.detalleAnulados
                .map((dia) => format(parseISO(`${dia}T00:00:00`), 'dd/MM/yyyy'))
                .join(', ')}`}
            </p>
          )}
        </div>

          <div className="text-center">
            <p className="text-lg mb-1 text-gris-piedra">Total extras:</p>
            <p className="text-3xl font-bold text-amarillo-dorado">
              {totalExtrasCantidadActivos.toLocaleString('es-AR', { minimumFractionDigits: 0 })} uds
            </p>
            <p className="text-lg font-semibold text-amarillo-dorado">
              ${formatCurrency(totalExtrasMontoActivos)}
            </p>
            <p className="text-xs text-gris-piedra">
              Ingresos por extras hoy: ${formatCurrency(totalExtrasIngresos)}
            </p>
            {(totalExtrasCantidadAnulados > 0 || totalExtrasMontoAnulados > 0) && (
              <p className="text-sm text-red-300 mt-1">
                Anulados: {totalExtrasCantidadAnulados.toLocaleString('es-AR', { minimumFractionDigits: 0 })} uds · ${formatCurrency(totalExtrasMontoAnulados)}
              </p>
            )}
          </div>
      </div>

      <div className="mb-6">
        <h3 className="text-xl font-semibold mb-4 text-center text-azul-claro">Desglose diario por medios de pago</h3>
        <div className="grid gap-4 md:grid-cols-2 mb-4">
          <div className="bg-blanco-puro bg-opacity-5 p-4 rounded-lg text-center">
            <p className="text-md text-gris-piedra mb-1 flex items-center justify-center">
              <DollarSign className="mr-2" /> Total en efectivo
            </p>
            <p className="text-2xl font-bold text-amarillo-dorado">
              ${formatCurrency(efectivoTotales.total)}
            </p>
            <p className="text-sm text-gris-piedra mt-1">
              Entradas: ${formatCurrency(efectivoTotales.entradas)} · Extras: ${formatCurrency(efectivoTotales.extras)}
            </p>
          </div>
          <div className="bg-blanco-puro bg-opacity-5 p-4 rounded-lg text-center">
            <p className="text-md text-gris-piedra mb-1 flex items-center justify-center">
              <CreditCard className="mr-2" /> Otros medios de pago
            </p>
            <p className="text-2xl font-bold text-azul-claro">
              ${formatCurrency(otrosTotales.total)}
            </p>
            <p className="text-sm text-gris-piedra mt-1">
              Entradas: ${formatCurrency(otrosTotales.entradas)} · Extras: ${formatCurrency(otrosTotales.extras)}
            </p>
          </div>
        </div>
        <div className="space-y-2">
          {detalleMedios.length === 0 && (
            <p className="text-sm text-center text-gris-piedra">
              Aún no hay información de medios de pago para hoy.
            </p>
          )}
          {detalleMedios.map((medio) => {
            const esEfectivoMedio = esMedioEfectivo(medio.nombre);
            return (
              <div
                key={medio.id ?? medio.nombre}
                className="flex flex-col md:flex-row md:items-center md:justify-between bg-blanco-puro bg-opacity-5 px-4 py-3 rounded-lg text-sm md:text-base"
              >
                <div className="font-semibold text-azul-claro flex items-center gap-2">
                  <span className={esEfectivoMedio ? 'text-amarillo-dorado' : 'text-azul-claro'}>
                    {medio.nombre}
                  </span>
                </div>
                <div className="flex flex-col md:flex-row md:items-center md:space-x-4 text-gris-piedra">
                  <span>Total: ${formatCurrency(medio.total || medio.monto)}</span>
                  <span>Entradas: ${formatCurrency(medio.entradasMonto)}</span>
                  <span>Extras: ${formatCurrency(medio.extrasMonto || medio.extras)}</span>
                  {Number(medio.totalAnulado || medio.montoAnulado || 0) > 0 && (
                    <span className="text-red-300">
                      Anulados: ${formatCurrency(medio.totalAnulado || medio.montoAnulado)}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mb-6">
        <h3 className="text-xl font-semibold mb-4 text-center text-azul-claro">Ventas por Usuario</h3>
        <div className="space-y-3">
          {ventasPorUsuario.map((usuario, index) => (
            <div key={index} className="flex justify-between items-center bg-blanco-puro bg-opacity-5 p-3 rounded-lg">
              <span className="text-lg">{usuario.nombre}</span>
              <div className="flex items-center space-x-4">
                <span className="text-lg font-bold text-azul-claro">{usuario.totalClientes} entradas</span>
                <span className="text-lg font-bold text-verde-esmeralda">{Number(usuario.totalDias || 0).toLocaleString('es-AR', { minimumFractionDigits: 0 })} días</span>
                <span className="text-lg font-bold text-amarillo-dorado">${formatCurrency(usuario.totalVentas)}</span>
                <span className="text-md text-amarillo-dorado">
                  Extras: {Number(usuario.totalExtrasCantidad || 0).toLocaleString('es-AR', { minimumFractionDigits: 0 })} uds · ${formatCurrency(usuario.totalExtras)}
                </span>
                {usuario.totalClientesAnulados > 0 && (
                  <span className="text-sm text-red-300">
                    Anuladas: {usuario.totalClientesAnulados} (${formatCurrency(usuario.totalVentasAnuladas)})
                  </span>
                )}
                {Number(usuario.totalDiasAnulados || 0) > 0 && (
                  <span className="text-sm text-red-300">
                    Días anulados: {Number(usuario.totalDiasAnulados || 0).toLocaleString('es-AR', { minimumFractionDigits: 0 })}
                  </span>
                )}
                {Number(usuario.totalExtrasAnulados || 0) > 0 && (
                  <span className="text-sm text-red-300">
                    Extras anulados: {Number(usuario.totalExtrasCantidadAnulados || 0).toLocaleString('es-AR', { minimumFractionDigits: 0 })} uds · ${formatCurrency(usuario.totalExtrasAnulados)}
                  </span>
                )}
                <button
                  onClick={() => onDesglose(usuario.id)}
                  className="px-3 py-1 bg-azul-oscuro hover:bg-blue-900 text-blanco-puro rounded text-sm"
                >
                  Desglose
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {(extrasActivos.length > 0 || extrasAnulados.length > 0) && (
        <div className="mt-8">
          <h3 className="text-xl font-semibold mb-4 text-center text-azul-claro">Detalle de extras</h3>
          <div className="grid gap-4 md:grid-cols-2">
            {extrasActivos.length > 0 && (
              <div className="bg-blanco-puro bg-opacity-5 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-verde-esmeralda mb-3">Activos</h4>
                <div className="space-y-2">
                  {extrasActivos.map((extra) => (
                    <div
                      key={`${extra.id ?? extra.nombre}-activo`}
                      className="flex justify-between text-sm md:text-base"
                    >
                      <span>{extra.nombre}</span>
                      <span className="font-semibold text-amarillo-dorado">
                        {`${Number(extra.cantidad || 0).toLocaleString('es-AR', { minimumFractionDigits: 0 })} uds · $${formatCurrency(extra.total)}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {extrasAnulados.length > 0 && (
              <div className="bg-blanco-puro bg-opacity-5 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-red-300 mb-3">Anulados</h4>
                <div className="space-y-2">
                  {extrasAnulados.map((extra) => (
                    <div
                      key={`${extra.id ?? extra.nombre}-anulado`}
                      className="flex justify-between text-sm md:text-base text-red-200"
                    >
                      <span>{extra.nombre}</span>
                      <span className="font-semibold">
                        {`${Number(extra.cantidad || 0).toLocaleString('es-AR', { minimumFractionDigits: 0 })} uds · $${formatCurrency(extra.total)}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ResumenDia;